import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../../api/axios";
import { useActiveLocation } from "../activeLocation";
import { PlatformsView } from "./PlatformsView";
import PlatformModal, { type PlatformPending, type SavePlatformPayload } from "./PlatformModal";
import { useAssignableUnits, usePlatforms } from "./usePlatforms";
import { Toast, errorMessage, useToast } from "../Toast";
import { Platform, PlatformUnit } from "./types";

interface PlatformsOptions {
  managementBase: string;
  maintenance?: boolean;
  platformSide?: boolean;
  tValue?: boolean;
  assignUnits?: boolean;
  currentWeight?: boolean;
}

export function createPlatforms({
  managementBase,
  maintenance = false,
  platformSide = false,
  tValue = false,
  assignUnits = false,
  currentWeight = false,
}: PlatformsOptions) {
  return function Platforms() {
    const { locationId, name } = useActiveLocation();
    const query = usePlatforms(locationId);
    const queryClient = useQueryClient();

    const [selectedPlatformId, setSelectedPlatformId] = useState<number | null>(null);
    const [errorText, setErrorText] = useState<string | null>(null);
    const { toast, showToast, dismissToast } = useToast();

    // Always read the freshest platform off the polled query so the modal's
    // status + stepper advance as data refetches.
    const selectedPlatform = selectedPlatformId != null
      ? query.data?.platforms.find((p) => p.id === selectedPlatformId) ?? null
      : null;

    const assignableUnitsQuery = useAssignableUnits(
      locationId,
      assignUnits && selectedPlatformId != null
    );

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["platforms", locationId] });
      queryClient.invalidateQueries({ queryKey: ["assignable-units", locationId] });
    };

    const onError = (error: unknown) => setErrorText(errorMessage(error));

    const requestMutation = useMutation({
      mutationFn: (unitId: number) => api.put(`/${managementBase}/retrieve-unit/${unitId}`),
      onError,
      onSettled: invalidate,
    });

    const returnMutation = useMutation({
      mutationFn: (unitId: number) => api.put(`/${managementBase}/return-unit/${unitId}`),
      onError,
      onSettled: invalidate,
    });

    const resetMutation = useMutation({
      mutationFn: (platformId: number) => api.post(`/platforms/${platformId}/reset`, { locationId }),
      onError,
      onSettled: invalidate,
    });

    // Save reports through the toast instead of the modal's inline alert, so the
    // outcome is visible whether or not the modal is still open.
    const saveMutation = useMutation({
      mutationFn: (payload: SavePlatformPayload) =>
        api.put(`/platforms/${selectedPlatformId}`, { locationId, ...payload }),
      onSuccess: () => showToast("success", "Platform saved."),
      onError: (error: unknown) => showToast("error", errorMessage(error)),
      onSettled: invalidate,
    });

    const moveToMaintenanceMutation = useMutation({
      mutationFn: (platformId: number) => api.put(`/${managementBase}/maintenance/${platformId}`),
      onError,
      onSettled: invalidate,
    });

    const returnFromMaintenanceMutation = useMutation({
      mutationFn: (platformId: number) =>
        api.put(`/${managementBase}/return-from-maintenance/${platformId}`),
      onError,
      onSettled: invalidate,
    });

    const pending: PlatformPending | null = requestMutation.isPending
      ? { action: "request", unitId: requestMutation.variables }
      : returnMutation.isPending
      ? { action: "return", unitId: returnMutation.variables }
      : resetMutation.isPending
      ? { action: "reset" }
      : saveMutation.isPending
      ? { action: "save" }
      : moveToMaintenanceMutation.isPending
      ? { action: "moveToMaintenance" }
      : returnFromMaintenanceMutation.isPending
      ? { action: "returnFromMaintenance" }
      : null;

    const openPlatform = (platform: Platform) => {
      setErrorText(null);
      setSelectedPlatformId(platform.id);
    };

    const closeModal = () => {
      setSelectedPlatformId(null);
      setErrorText(null);
    };

    return (
      <>
        <PlatformsView
          data={query.data}
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          locationLabel={name}
          onSelectPlatform={openPlatform}
          platformSide={platformSide}
          tValue={tValue}
          currentWeight={currentWeight}
        />

        {selectedPlatform && (
          <PlatformModal
            opened
            onClose={closeModal}
            platform={selectedPlatform}
            unitTypes={query.data?.unitTypes ?? []}
            assignableUnits={assignableUnitsQuery.data ?? []}
            assignableUnitsLoading={assignableUnitsQuery.isPending}
            assignableUnitsError={assignableUnitsQuery.isError}
            assignUnits={assignUnits}
            tValue={tValue}
            platformSide={platformSide}
            onRequest={(unit: PlatformUnit) => {
              setErrorText(null);
              requestMutation.mutate(unit.unitId);
            }}
            onReturn={(unit: PlatformUnit) => {
              setErrorText(null);
              returnMutation.mutate(unit.unitId);
            }}
            onReset={() => {
              setErrorText(null);
              resetMutation.mutate(selectedPlatform.id);
            }}
            onSave={(payload: SavePlatformPayload) => {
              setErrorText(null);
              saveMutation.mutate(payload);
            }}
            onMoveToMaintenance={
              maintenance
                ? () => {
                    setErrorText(null);
                    moveToMaintenanceMutation.mutate(selectedPlatform.id);
                  }
                : undefined
            }
            onReturnFromMaintenance={
              maintenance
                ? () => {
                    setErrorText(null);
                    returnFromMaintenanceMutation.mutate(selectedPlatform.id);
                  }
                : undefined
            }
            pending={pending}
            errorText={errorText}
            onDismissError={() => setErrorText(null)}
          />
        )}

        <Toast toast={toast} onDismiss={dismissToast} />
      </>
    );
  };
}
